import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Tooltip, Space, message, Spin, MenuProps, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import Icon, {
	DeleteOutlined,
	FolderOpenOutlined,
	ExclamationCircleOutlined,
	EditOutlined,
	CopyOutlined,
	LoadingOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { PublicationState, SharedWithUser } from '@kemu-io/kemu-types';
import { ReactSVG } from 'react-svg';
import { removeRecipe, setAsyncOperationDetails, asyncOperationState } from '../../features/interface/interfaceSlice';
import { unpublishRecipeState } from '../layout/marketplace/marketplaceSlice';
import { AsyncRequestStatus, AsyncState, RecipeResponse } from '../../types/core_t';
import { selectCurrentRecipe } from '../../features/Workspace/workspaceSlice';
import useAlert from '../alert/useAlert';
import VerticalDots from '../verticalDots/verticalDots';
import recipeApi from '../../api/recipe/recipeApi';
import CardRibbon from '../marketplace/CardRibbon/CardRibbon';
import useTranslation from '../../common/hooks/useTranslation';
import useMarkedText from '../../common/hooks/useMarkedText';
import { USER_CHAT_PROPERTIES } from '../../common/constants';
import getFreshChatWidget from '../../app/recipe/freshChatWidget';
import { ReactComponent as TutorialsIcon } from '../layout/marketplace/MarketplaceMenu/tutorials.svg';
import routes from '../../common/routes/index';
import { downloadRecipeAction } from '../../features/interface/reducers/downloadRecipeReducer';
import useNavigateWithSearch from '../../common/hooks/useNavigateWithSearch';
import styles from './RecipeCard.module.css';

export type RecipeCardDetails = {
	shareList?: SharedWithUser[],
	publicShareId?: string | null
}

interface Props {
	onForceReload?: ()=> void;
	onShowPublisher?: (recipe: RecipeResponse)=> void;
	onRename: (recipe: RecipeResponse)=> void;
	onRecipeDeleted?: (recipeId: string) => void;
	selected?: boolean;
	recipe: RecipeResponse;
}

const statesWithRibbon: PublicationState[] = ['pending', 'published', 'rejected'];

function RecipeCard (props: Props): React.JSX.Element | null {
	const Alert = useAlert();
	const t = useTranslation();
	const dispatch = useDispatch();
	const parseHtml = useMarkedText();
	const navigateWithSearch = useNavigateWithSearch();
	// const [linkingTutorial, setLinkingTutorial] = useState(false);
	const chatWidget = getFreshChatWidget();

	// Create slice for individual operations (rename, delete)
	const [asyncOperation, setAsyncOperation] = useState<AsyncState>({
		status: AsyncRequestStatus.idle
	});

	const { onForceReload, onShowPublisher, onRecipeDeleted } = props;
	const { id: recipeID, name: recipeName, sharedWith, publicSharedId, updatedAt, author, linkedTutorial } = props.recipe;
	const recipeInfo = useSelector(selectCurrentRecipe);
	const unpublishingState = useSelector(unpublishRecipeState);
	const asyncState = useSelector(asyncOperationState);

	const asyncOperationOwner = asyncState?.entityId === recipeID;
	const deletionInProgress = asyncState?.status === AsyncRequestStatus.loading && asyncOperationOwner;
	const deletionSuccessful = asyncState?.status === AsyncRequestStatus.completed && asyncOperationOwner;
	const deletionErrorReason = asyncState?.status === AsyncRequestStatus.error ? asyncState.error?.message || 'Unknown': null;

	const navigate = useNavigate();
	const date = updatedAt ? parseISO(updatedAt) : new Date();
	const createdSince = formatDistanceToNow(date) + ' ago';
	const protocolVersion = Number(props.recipe.protocolVersion || 0);
	const tooOld = protocolVersion < 1.6;


	const onDelete = () => {
		dispatch(setAsyncOperationDetails({
			entityId: recipeID,
			action: 'delete'
		}));

		dispatch(removeRecipe({
			recipeId: recipeID,
			onSuccess: () => {
				// Immediately remove from local search results for instant UI feedback
				if (onRecipeDeleted) {
					const msgKey = 'deletionIndicator';
					message.success({ content: t('DeleteRecipeIndicator.Success', 'Recipe Deleted!'), duration: 3, key: msgKey });
					onRecipeDeleted(recipeID);
				}
			},
		}));
	};

	const handleDeleteConfirm = () => {
		if (props.recipe?.publication?.state === 'pending' || props.recipe?.publication?.state === 'published') {
			return Alert.info({
				title: t('RecipeDeletionDialog.PublicationExists.Title', 'Cannot Delete Recipe'),
				iconColor: 'info',
				content: t('RecipeDeletionDialog.PublicationExists.Content', 'You need to \'Unpublish\' this recipe first.'),
				okText: t('RecipeDeletionDialog.PublicationExists.BtnClose', 'Close'),
				okButtonColor: 'primary',
			});
		}

		Alert.confirm({
			title: t('RecipeDeletionDialog.Title', 'Delete Recipe'),
			iconColor: 'warning',
			content: t('RecipeDeletionDialog.Content', 'Are you sure you want to delete "{name}"?',  { name: recipeName } ),
			okText: t('RecipeDeletionDialog.BtnOk', 'Delete'),
			okButtonColor: 'danger',
			cancelText: t('RecipeDeletionDialog.BtnCancel', 'Cancel'),
			onOk: onDelete
		});
	};

	const proceedIfSafe = (next: ()=> void) => {
		if (recipeInfo.needsSaving) {
			Alert.error({
				title: t('RecipeUnsavedWarning.Title', 'Merge Recipe'),
				icon: <ExclamationCircleOutlined />,
				content: t('RecipeUnsavedWarning.Content', 'You have unsaved changes in "{name}". If you proceed your changes will be lost.',  { name: recipeInfo.name } ),
				okText: t('RecipeUnsavedWarning.BtnOk', 'Proceed'),
				okButtonColor: 'warning',
				cancelText: t('RecipeUnsavedWarning.BtnCancel', 'Cancel'),
				onOk: next
			});
		} else {
			next();
		}
	};


	const handleCloning = useCallback(async () => {
		try {
			setAsyncOperation({ status: AsyncRequestStatus.loading });
			const createdRecipe = await recipeApi.cloneRecipe(recipeID, `${recipeName} copy`);
			console.log('Cloned recipe: ', createdRecipe);
			onForceReload && onForceReload();
		} catch (e) {
			message.error({ content: `Error cloning recipe: ${e.message || ''}`, duration: 3 });
			setAsyncOperation({ status: AsyncRequestStatus.completed, error: e });
		}
	}, [recipeID, recipeName, onForceReload]);




	/** Open recipe */
	const handleOpen = () => {
		proceedIfSafe(() => {
			if (chatWidget.initialized) {
				chatWidget.setUserProperty({
					[USER_CHAT_PROPERTIES.LastOpenedRecipeId]: recipeID,
					[USER_CHAT_PROPERTIES.LastOpenedRecipeName]: recipeName,
				});
			}

			dispatch(downloadRecipeAction({ recipeId: recipeID, navigate, alert: Alert }));
		});
	};

	const showRejectionInfoDialog = () => {
		const getCleanComment = () => {
			if (props.recipe.publication?.audit) {
				return parseHtml(props.recipe.publication.audit.message);
			}

			return '';
		};

		if (props.recipe.publication?.state === 'rejected' && props.recipe.publication?.audit) {
			Alert.info({
				title: t('RecipeRevision.Rejected.Title', 'Publication Rejected'),
				icon: <ExclamationCircleOutlined />,
				content: <>
					<span dangerouslySetInnerHTML={{ __html: getCleanComment() }} />
				</>,
				okText: t('RecipeRevision.Rejected.CloseBtn', 'Close'),
			});
		}
	};

	const versionItems = useMemo((): MenuProps['items'] => {
		const history = props.recipe.history;
		if (!history || history.length === 0) { return undefined; }

		const sorted = [...history].sort((a, b) => {
			const ad = new Date(a.createdAt).getTime();
			const bd = new Date(b.createdAt).getTime();
			return bd - ad; // newest first
		});

		return sorted.map((item) => ({
			key: `v-${item.version}`,
			disabled: item.version === props.recipe.version,
			label: (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<strong>v{item.version}</strong>
						{item.version === props.recipe.version && (
							<span style={{ fontSize: 11, color: '#52c41a' }}>(latest)</span>
						)}
					</div>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						{new Date(item.createdAt).toLocaleString()}
					</Typography.Text>
					{item.comments && (
						<Typography.Text ellipsis style={{ maxWidth: 260 }}>
							{item.comments}
						</Typography.Text>
					)}
				</div>
			),
		}));
	}, [props.recipe.history, props.recipe.version]);

	const onSelectVersion = useCallback((info: { key: string }) => {
		const v = Number(String(info.key).replace('v-', ''));
		if (!Number.isFinite(v) || v === props.recipe.version) { return; }

		proceedIfSafe(() => {
			if (chatWidget.initialized) {
				chatWidget.setUserProperty({
					[USER_CHAT_PROPERTIES.LastOpenedRecipeId]: props.recipe.id,
					[USER_CHAT_PROPERTIES.LastOpenedRecipeName]: props.recipe.name,
				});
			}

			dispatch(downloadRecipeAction({
				recipeId: props.recipe.id,
				version: v,
				alert: Alert,
				navigate: navigate,
			}));
		});
	}, [props.recipe.id, props.recipe.version, props.recipe.name, dispatch, Alert, navigate, chatWidget]);

	const menu: MenuProps = {
		className: styles.DropdownMenu,
		items: [
			{
				key: 'rename',
				icon: <EditOutlined />,
				label: t('Marketplace.MyRecipes.Card.Menu.Rename'),
				onClick: () => props.onRename(props.recipe),
			},
			...(versionItems ? [{
				key: 'versions',
				icon: <CopyOutlined />,
				label: t('Marketplace.MyRecipes.Card.Menu.Versions', 'Versions'),
				children: versionItems,
			}] : []),
			// TODO: restore once new BE endpoint is ready
			// {
			// 	key: 'duplicate',
			// 	icon: <CopyOutlined />,
			// 	label: t('Marketplace.MyRecipes.Card.Menu.Duplicate'),
			// 	onClick: handleCloning,
			// },
		],
		onClick: (info) => {
			if (info.key.startsWith('v-')) {
				onSelectVersion(info);
			}
		},
	};


	useEffect(() => {
		const msgKey = 'deletionIndicator';
		if (asyncOperationOwner) {
			if (deletionInProgress) { message.loading({ content: t('DeleteRecipeIndicator.Progress', 'Deleting Recipe...'), duration: 0, key: msgKey }); }
			if (deletionSuccessful) { message.success({ content: t('DeleteRecipeIndicator.Success', 'Recipe Deleted!'), duration: 3, key: msgKey }); }
			if (deletionErrorReason) {
				message.error({
					content: t('DeleteRecipeIndicator.Failure', 'Failed to delete recipe') + `: ${deletionErrorReason}`,
					duration: 3,
					key: msgKey
				});
			}
		}
	}, [deletionInProgress, deletionSuccessful, asyncOperationOwner, recipeID, t, deletionErrorReason]);

	const PublishedRibbon = (): React.JSX.Element => {
		const intlMsgByState: Record<string, string> = {
			published: 'Marketplace.MyRecipes.CardRibbon.Published',
			pending: 'Marketplace.MyRecipes.CardRibbon.Pending',
			rejected: 'Marketplace.MyRecipes.CardRibbon.Rejected',
		};

		let ribbonMsg = t(intlMsgByState[props.recipe.publication?.state || ''], '');
		if (props.recipe.publication && props.recipe.publication.version > 1) {
			ribbonMsg = `${ribbonMsg} v${props.recipe.publication?.version}`;
		}

		return (
			<CardRibbon
				state={props.recipe.publication?.state}
				text={
					<>
						<Link to={routes.marketplace.getPublicationDetailsRoute('recipe', props.recipe.publication?.id || '')}>
							{ribbonMsg}
						</Link>
						{props.recipe.publication?.state === 'rejected' && (
							<Tooltip title={t('Marketplace.MyRecipes.Card.RejectedMoreInfoBtn', 'More info')}>
								<span className={styles.RejectedInfoBtn} onClick={showRejectionInfoDialog}>
									<InfoCircleOutlined />
								</span>
							</Tooltip>
						)}
					</>
				}
			>
				<CardHeader />
			</CardRibbon>
		);

	};


	const getDynamicSVG = (url: string) => {
		return () => <ReactSVG src={url} className="dynamic"/>;
	};

	const NewRibbon = (): React.JSX.Element => {
		return (
			<CardRibbon color="#1fc314" text={t('Marketplace.MyRecipes.CardRibbon.New', 'New')}>
				<CardHeader />
			</CardRibbon>
		);
	};

	const CardHeader = (): React.JSX.Element => {
		return (
			<div className={styles.Header}>
				<div className={styles.HeaderIcons}>
					{props.recipe.blocks.map((block, i) => (
						<span key={`${block.id}_${i}`}>
							{/* Only show up to 5 icons */}
							{i > 3 ? null : (
								<Icon component={getDynamicSVG(`${routes.recipe.getStaticAssetsRoute(block.type, block.version)}/icon.svg`)} />
							)}
						</span>
					))}
				</div>
		</div>
		);
	};


	return (
		<>
			<article className={`${styles.RecipeBody} ${props.selected ? 'selected' : ''}`} data-kemu-meta="recipe-card">
				<Spin
					indicator={<LoadingOutlined />}
					spinning={
						asyncOperation.status === AsyncRequestStatus.loading ||
						(unpublishingState.publicationId === props.recipe.publication?.id && unpublishingState.asyncState.status === AsyncRequestStatus.loading)
					}
				>

					{props.recipe.publication && statesWithRibbon.includes(props.recipe.publication.state) ? (
						<PublishedRibbon />
					): props.selected ? (
						<NewRibbon />
					): (
						<CardHeader />
					)}

					<div className={styles.Details}>
						<div className={styles.Title}>
							<h2>
								{linkedTutorial && (
									<span className={styles.TutorialIcon}>
										<Link to={routes.tutorial.getTutorialRoute(linkedTutorial, 0)}>
											<Tooltip title={t('Marketplace.MyRecipes.Card.LinkedTutorialTooltip', 'Click to open linked tutorial' )}>
												<Icon component={TutorialsIcon} />
											</Tooltip>
										</Link>
									</span>
								)}

								{recipeName}
							</h2>
						</div>
						<div className={styles.Category}>
							<div className={styles.UserName}>{author?.name || 'Unknown'}</div>
							<div className={styles.TimeContainer}>
								{/* <div className={styles.Bullet} /> */}
								<div className={styles.DateInfo}>
									{createdSince}
								</div>
							</div>

						</div>
						<Space size={10}>
							<Tooltip
								destroyTooltipOnHide={true}
								title={ tooOld ? t('Marketplace.MyRecipes.Card.TooOld', 'Recipe not compatible') : t('Marketplace.MyRecipes.Card.OpenBtn', 'Open')}
							>
								<Button
									data-kemu-meta="open-recipe"
									disabled={deletionInProgress || tooOld}
									onClick={handleOpen}
									type="primary"
									className={styles.CircleButton}
									shape="circle" icon={<FolderOpenOutlined />}
								/>
							</Tooltip>

							{/* <Tooltip destroyTooltipOnHide={true} title={<FormattedMessage id="Marketplace.MyRecipes.Card.ShareBtn" defaultMessage="Share recipe"/>}>
								<Button data-kemu-meta="share-recipe" disabled={deletionInProgress} onClick={() => setShareDialogVisible(true)} type="primary" className={styles.CircleButton} shape="circle" icon={<ShareAltOutlined />} />
							</Tooltip> */}

							{/* Recipe publishing disabled for now */}
							{/* <Tooltip
								destroyTooltipOnHide={true}
								title={
									props.recipe.publication ? (
										<FormattedMessage id="Marketplace.MyRecipes.Card.PublishVersion" defaultMessage="Publish new version"/>
									) : (
										<FormattedMessage id="Marketplace.MyRecipes.Card.Publish" defaultMessage="Publish recipe"/>
									)
								}
							>
								<Button
									data-kemu-meta="publish-recipe"
									disabled={deletionInProgress}
									onClick={handlePublishRecipe}
									type="primary"
									className={styles.CircleButton}
									shape="circle"
									icon={<Icon component={PublishIcon} className={styles.CustomSvg} />}
								/>
							</Tooltip> */}

							{/* <Tooltip destroyTooltipOnHide={true} title={<FormattedMessage id="Marketplace.MyRecipes.Card.MergeBtn" defaultMessage="Merge into current recipe"/>}>
								<Button disabled={deletionInProgress} onClick={handleCombine} type="primary" className={styles.CircleButton} shape="circle" icon={<Icon component={MergeIcon} className={styles.CustomSvg} />} />
							</Tooltip> */}
							{/* <Tooltip destroyTooltipOnHide={true} title={<FormattedMessage id="Marketplace.MyRecipes.Card.DownloadBtn" defaultMessage="Download"/>}>
								<Button disabled={deletionInProgress} type="primary" className={styles.CircleButton} shape="circle" icon={<DownloadOutlined />} />
							</Tooltip> */}
							<Tooltip destroyTooltipOnHide={true} title={<FormattedMessage id="Marketplace.MyRecipes.Card.DeleteBtn" defaultMessage="Delete"/>}>
								<Button data-kemu-meta="delete-recipe" disabled={deletionInProgress} onClick={handleDeleteConfirm} type="primary" danger className={styles.CircleButton} shape="circle" icon={<DeleteOutlined />} />
							</Tooltip>
						</Space>
					</div>

					<VerticalDots menu={menu} />
				</Spin>
			</article>
		</>
	);
}

	export default RecipeCard;

